/**
 * Shared account-usage fetch for the proactive banner surfaces
 * ({@link UsageNudge}, {@link BudgetResetBanner}).
 *
 * Fetches the account-wide `GET /account/usage` rollup on mount and re-fetches
 * whenever a budget/limit response invalidates usage (the 429/403 handled by
 * the HTTP interceptor fires {@link onUsageInvalidated}) — so every consumer
 * converges on the SAME live picture without waiting for a page refresh.
 * Silent on failure: these banners are purely additive, so an unreadable
 * rollup simply reads as `null` and the consumer renders conservatively.
 *
 * The hook reports a `settled` flag alongside the rollup so a consumer can tell
 * "the fetch has not answered yet" (still loading) from "the fetch answered
 * with no usable rollup" (empty/failed). That distinction matters for the
 * positive {@link BudgetResetBanner}: while the fetch is in flight `usage` is
 * `null`, which looks the same as "nothing is tight" — so without the flag the
 * banner would flash a "budget refreshed" cheer for the load window and then
 * yank it away the instant a spent-budget rollup arrives. Gating on `settled`
 * removes that flash.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { useHttpClient } from '@molecule/app-react'

import { onUsageInvalidated, type UsageResponse } from '../utilities/usage-utilities.js'

/** The live account-usage rollup plus whether the first fetch has resolved. */
export interface AccountUsageState {
  /** Live account usage rollup, or `null` while loading / on failure. */
  usage: UsageResponse | null
  /**
   * True once the first fetch has resolved (successfully or not). A consumer
   * that must not render before it knows the live picture waits on this; while
   * `false`, `usage` is `null` purely because the request is still in flight.
   */
  settled: boolean
}

/**
 * Live account usage rollup plus a `settled` flag.
 * @returns The latest usage response and whether the first fetch has resolved.
 */
export function useAccountUsage(): AccountUsageState {
  const http = useHttpClient()
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [settled, setSettled] = useState(false)

  const mountedRef = useRef(true)
  useEffect(
    () => () => {
      mountedRef.current = false
    },
    [],
  )
  const refetch = useCallback(() => {
    http
      .get<UsageResponse>('/account/usage')
      .then((res) => {
        if (!mountedRef.current) return
        // A malformed 200 must not wipe a rollup already on screen.
        const body = res.data as unknown
        if (body && typeof body === 'object') setUsage(body as UsageResponse)
      })
      .catch(() => {
        // Keep whatever is already shown; only the initial load stays null.
      })
      .finally(() => {
        if (mountedRef.current) setSettled(true)
      })
  }, [http])
  useEffect(() => {
    refetch()
  }, [refetch])
  useEffect(() => onUsageInvalidated(refetch), [refetch])

  return { usage, settled }
}
